import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MarginDialog from "../dialogs/MarginDialog";
import {
  useDeleteMarginPeriodMutation,
  useUpdateMarginPeriodMutation,
  useAddMarginPeriodMutation,
  metricsApi,
} from "../../store/apis/metricsApi";

// Define the types for raw margin periods and inputs
type CampaignMarginPeriod = components["schemas"]["CampaignMarginPeriod"];
type CreateMarginPeriodInput = components["schemas"]["CreateMarginPeriodInput"];

interface InternalCampaignDbMarginTableProps {
  rawMargins: CampaignMarginPeriod[];
  isLoading: boolean;
  error: unknown;
  internalCampaignId: string;
  startDate: string | null;
  endDate: string | null;
  showInternalCampaignColumn?: boolean;
}

const InternalCampaignDbMarginTable: React.FC<
  InternalCampaignDbMarginTableProps
> = ({
  rawMargins,
  isLoading,
  error,
  internalCampaignId,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [
    deleteMarginPeriod,
    { isLoading: isDeleting, isSuccess: isDeletingSuccess, error: deleteError },
  ] = useDeleteMarginPeriodMutation();
  const [
    updateMarginPeriod,
    { isLoading: isUpdating, isSuccess: isUpdatingSuccess, error: updateError },
  ] = useUpdateMarginPeriodMutation();
  const [
    addMarginPeriod,
    { isLoading: isAdding, isSuccess: isAddingSuccess, error: addError },
  ] = useAddMarginPeriodMutation();

  // State for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentMargin, setCurrentMargin] =
    useState<CampaignMarginPeriod | null>(null);

  // State for add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [marginToDelete, setMarginToDelete] = useState<string | null>(null);

  const handleEditClick = (margin: CampaignMarginPeriod) => {
    setCurrentMargin(margin);
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (marginId: string) => {
    setMarginToDelete(marginId);
    setDeleteDialogOpen(true);
  };

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false);
    setCurrentMargin(null);
  }, []);

  const handleAddClose = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setMarginToDelete(null);
  }, []);

  // Effect to handle update success and error states
  React.useEffect(() => {
    if (isUpdatingSuccess) {
      // Force refetch of both calculated and raw margins
      dispatch(metricsApi.util.invalidateTags(["Metrics"]));

      // Show success message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginUpdatedSuccess",
            "Margin period updated successfully"
          ),
          severity: "success",
        })
      );

      handleEditClose();
    }

    if (updateError) {
      // Show error message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginUpdateError",
            "Failed to update margin period: {{error}}",
            {
              error:
                updateError instanceof Error
                  ? updateError.message
                  : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isUpdatingSuccess, updateError, dispatch, t, handleEditClose]);

  const handleSaveEdit = (
    marginData: CreateMarginPeriodInput
  ): Promise<void> => {
    if (!currentMargin) return Promise.resolve();

    return updateMarginPeriod({
      id: currentMargin.id,
      marginPeriod: marginData,
    })
      .unwrap()
      .then(() => {
        // Success is handled in the useEffect hook
      })
      .catch((err) => {
        // Error is handled in the useEffect hook
        console.error("Failed to update margin period:", err);
      });
  };

  // Effect to handle add success and error states
  React.useEffect(() => {
    if (isAddingSuccess) {
      // Force refetch of both calculated and raw margins
      dispatch(metricsApi.util.invalidateTags(["Metrics"]));

      // Show success message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginAddedSuccess",
            "Margin period added successfully"
          ),
          severity: "success",
        })
      );

      handleAddClose();
    }

    if (addError) {
      // Show error message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginAddError",
            "Failed to add margin period: {{error}}",
            {
              error:
                addError instanceof Error ? addError.message : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isAddingSuccess, addError, dispatch, t, handleAddClose]);

  const handleSaveAdd = (
    marginData: CreateMarginPeriodInput
  ): Promise<void> => {
    return addMarginPeriod({
      id: internalCampaignId,
      marginPeriod: marginData,
    })
      .unwrap()
      .then(() => {
        // Success is handled in the useEffect hook
      })
      .catch((err) => {
        // Error is handled in the useEffect hook
        console.error("Failed to add margin period:", err);
      });
  };

  // Effect to handle delete success and error states
  React.useEffect(() => {
    if (isDeletingSuccess) {
      // Force refetch of both calculated and raw margins
      dispatch(metricsApi.util.invalidateTags(["Metrics"]));

      // Show success message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginDeletedSuccess",
            "Margin period deleted successfully"
          ),
          severity: "success",
        })
      );

      handleDeleteClose();
    }

    if (deleteError) {
      // Show error message
      dispatch(
        setToastMessage({
          message: t(
            "metrics.marginDeleteError",
            "Failed to delete margin period: {{error}}",
            {
              error:
                deleteError instanceof Error
                  ? deleteError.message
                  : "Unknown error",
            }
          ),
          severity: "error",
        })
      );
    }
  }, [isDeletingSuccess, deleteError, dispatch, t, handleDeleteClose]);

  const handleConfirmDelete = (): Promise<void> => {
    if (!marginToDelete) return Promise.resolve();

    return deleteMarginPeriod({ id: marginToDelete })
      .unwrap()
      .then(() => {
        // Success is handled in the useEffect hook
      })
      .catch((err) => {
        // Error is handled in the useEffect hook
        console.error("Failed to delete margin period:", err);
      });
  };

  // Add button component to be reused
  const AddButton = () => (
    <Tooltip title={t("metrics.addMargin", "Add Margin Period")}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddClick}
      >
        {t("common.add", "Add")}
      </Button>
    </Tooltip>
  );

  if (isLoading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("metrics.rawMarginData", "Raw Margin Data")}
          </Typography>
          <AddButton />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("metrics.rawMarginData", "Raw Margin Data")}
          </Typography>
          <AddButton />
        </Box>
        <Typography color="error">
          {t("common.errorLoading", "Error loading data")}
        </Typography>
      </>
    );
  }

  if (!rawMargins || rawMargins.length === 0) {
    return (
      <>
        <MarginDialog
          open={addDialogOpen}
          onClose={handleAddClose}
          onSave={handleSaveAdd}
          isLoading={isAdding}
          title={t("metrics.addMargin", "Add Margin Period")}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("metrics.rawMarginData", "Raw Margin Data")}
          </Typography>
          <AddButton />
        </Box>
        <Box sx={{ mt: 2, p: 3, textAlign: "center" }}>
          <Typography variant="body1">
            {t(
              "metrics.noMarginData",
              "No margin data available for this internal campaign."
            )}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("metrics.rawMarginData", "Raw Margin Data")}
        </Typography>
        <AddButton />
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="raw margins table">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.background.default,
              }}
            >
              <TableCell>{t("common.id", "ID")}</TableCell>
              <TableCell>
                {t("metrics.marginTable.startDate", "Start Date")}
              </TableCell>
              <TableCell>
                {t("metrics.marginTable.endDate", "End Date")}
              </TableCell>
              <TableCell align="right">
                {t("metrics.marginTable.margin", "Margin")} (%)
              </TableCell>
              <TableCell>{t("common.createdAt", "Created At")}</TableCell>
              <TableCell>{t("common.updatedAt", "Updated At")}</TableCell>
              <TableCell align="center">
                {t("common.actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rawMargins.map((margin) => (
              <TableRow
                key={margin.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body2">{margin.id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.startDate).format("MMM D, YYYY")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.endDate).format("MMM D, YYYY")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {margin.margin.toFixed(2)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.createdAt).format("MMM D, YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(margin.updatedAt).format("MMM D, YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(margin)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(margin.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <MarginDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        onSave={handleSaveEdit}
        margin={currentMargin || undefined}
        isLoading={isUpdating}
        title={t("metrics.editMargin", "Edit Margin Period")}
        campaignStartDate={startDate}
        campaignEndDate={endDate}
      />

      {/* Add Dialog */}
      <MarginDialog
        open={addDialogOpen}
        onClose={handleAddClose}
        onSave={handleSaveAdd}
        isLoading={isAdding}
        title={t("metrics.addMargin", "Add Margin Period")}
        campaignStartDate={startDate}
        campaignEndDate={endDate}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>
          {t("metrics.deleteMargin", "Delete Margin Period")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "metrics.deleteMarginConfirmation",
              "Are you sure you want to delete this margin period? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={24} />
            ) : (
              t("common.delete", "Delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InternalCampaignDbMarginTable;
